import { useState, type FC } from 'react';
import { useCustomMutation } from '@refinedev/core';
import { message } from 'antd';
import { Sparkles, Upload as UploadIcon, Image } from 'lucide-react';
import ImagePreview from './ImagePreview';
import ImageGenerateControls from './ImageGenerateControls';

interface BlogImageEditorProps {
  articleId: string;
  currentImageUrl: string | null;
  articleTitle: string;
  seoKeywords: string[];
  onImageGenerated: (imageUrl: string) => void;
}

type Mode = 'generate' | 'upload';

const BlogImageEditor: FC<BlogImageEditorProps> = ({
  articleId,
  currentImageUrl,
  articleTitle,
  seoKeywords,
  onImageGenerated,
}) => {
  const { mutate: generateImage } = useCustomMutation();

  const [mode, setMode] = useState<Mode>('generate');
  const [generating, setGenerating] = useState(false);
  const [model, setModel] = useState('gemini-2.5-flash-image');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [imageSize, setImageSize] = useState('1K');
  const [styleHint, setStyleHint] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl);
  const [lastTokens, setLastTokens] = useState<{ input: number; outputImage: number; total: number } | null>(null);
  const [totalGenerations, setTotalGenerations] = useState(0);
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);

  const handleGenerate = () => {
    setGenerating(true);
    setLastTokens(null);
    generateImage(
      {
        url: `/api/v1/admin/marketing/blog/${articleId}/generate-image`,
        method: 'post',
        values: { model, aspectRatio, imageSize, styleHint: styleHint || undefined },
      },
      {
        onSuccess: (res) => {
          const raw = res?.data as Record<string, unknown> | undefined;
          const d = (raw?.imageUrl ? raw : (raw?.data as Record<string, unknown> | undefined)) ?? {};
          const url = (d.imageUrl as string) ?? null;
          const tokens = d.tokensUsed as { input: number; outputImage: number; total: number } | undefined;
          if (url) { setPreviewUrl(url); onImageGenerated(url); }
          if (tokens) { setLastTokens(tokens); setTotalTokensUsed((prev) => prev + tokens.total); }
          setTotalGenerations((prev) => prev + 1);
          setGenerating(false);
          message.success('Image générée avec succès');
        },
        onError: (err) => { setGenerating(false); message.error(err?.message || 'Erreur lors de la génération'); },
      },
    );
  };

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-600">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          <Image className="mr-1.5 inline h-3.5 w-3.5" />
          Image à la une
        </p>
        <div className="flex rounded-md border border-slate-200 dark:border-slate-600">
          <button
            onClick={() => setMode('upload')}
            className={`px-3 py-1 text-xs transition-colors ${
              mode === 'upload'
                ? 'bg-slate-100 font-medium text-slate-700 dark:bg-slate-700 dark:text-white'
                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            <UploadIcon className="mr-1 inline h-3 w-3" />
            Upload
          </button>
          <button
            onClick={() => setMode('generate')}
            className={`px-3 py-1 text-xs transition-colors ${
              mode === 'generate'
                ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            <Sparkles className="mr-1 inline h-3 w-3" />
            Génération IA
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        <ImagePreview previewUrl={previewUrl} lastTokens={lastTokens} />
        <ImageGenerateControls
          mode={mode}
          generating={generating}
          model={model}
          aspectRatio={aspectRatio}
          imageSize={imageSize}
          styleHint={styleHint}
          previewUrl={previewUrl}
          articleTitle={articleTitle}
          seoKeywords={seoKeywords}
          totalGenerations={totalGenerations}
          totalTokensUsed={totalTokensUsed}
          onModelChange={setModel}
          onAspectRatioChange={setAspectRatio}
          onImageSizeChange={setImageSize}
          onStyleHintChange={setStyleHint}
          onGenerate={handleGenerate}
          onLocalImageLoad={(url) => setPreviewUrl(url)}
        />
      </div>
    </div>
  );
};

export default BlogImageEditor;
